import React, { RefObject, useCallback, useRef } from 'react'
import CruiseLineSelect, { CruiseLineSelectRef } from './CruiseLineSelect/CruiseLineSelect'
import CruiseWhenSelect, { CruiseWhenSelectRef } from './CruiseWhenSelect/CruiseWhenSelect'
import CruiseDestinationFilter, { CruiseLocationFilterRef } from './CruiseLocationFilter/CruiseLocationFilter'
import noop from 'lib/function/noop'
import CruiseDepartureSelect, { CruiseDepartureSelectRef } from 'components/Cruises/SearchPage/Filters/Inputs/CruiseDepartureSelect/CruiseDepartureSelect'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'

interface Props {
  onChange?: (values: App.CruiseGlobalFilters) => void
  /**
   * Necessary for the non-`drawerMode` to work
   */
  dropdownAnchorRef?: RefObject<HTMLElement | null>
  drawerMode?: boolean
  isCruiselineLandingPage?: boolean
}

function CruiseSearchInputs(props: Props) {
  const { drawerMode, onChange = noop, dropdownAnchorRef, isCruiselineLandingPage } = props
  const destinationRef = useRef<CruiseLocationFilterRef>(null)
  const departureRef = useRef<CruiseDepartureSelectRef>(null)
  const cruiseLineRef = useRef<CruiseLineSelectRef>(null)
  const whenRef = useRef<CruiseWhenSelectRef>(null)

  const autoFocusEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.cruiseAutoFocusNextFilter)

  const onDestinationChange = useCallback((values: App.CruiseGlobalFilters) => {
    onChange(values)
    if (autoFocusEnabled) {
      departureRef.current?.focus()
    }
  }, [onChange, autoFocusEnabled])

  const onDepartureChange = useCallback((values: App.CruiseGlobalFilters) => {
    onChange(values)
    if (autoFocusEnabled) {
      if (!isCruiselineLandingPage && cruiseLineRef.current) {
        cruiseLineRef.current.focus()
      } else {
        whenRef.current?.focus()
      }
    }
  }, [onChange, autoFocusEnabled, isCruiselineLandingPage])

  const onLineChange = useCallback((values: App.CruiseGlobalFilters) => {
    onChange(values)
    if (autoFocusEnabled) {
      whenRef.current?.focus()
    }
  }, [onChange, autoFocusEnabled])

  return <>
    <CruiseDestinationFilter
      ref={destinationRef}
      onChange={onDestinationChange}
      drawerMode={drawerMode}
      dropdownAnchorRef={dropdownAnchorRef}
    />

    <CruiseDepartureSelect
      ref={departureRef}
      onChange={onDepartureChange}
      drawerMode={drawerMode}
      dropdownAnchorRef={dropdownAnchorRef}
    />

    {!isCruiselineLandingPage && <CruiseLineSelect
      ref={cruiseLineRef}
      onChange={onLineChange}
      dropdownAnchorRef={dropdownAnchorRef!}
      drawerMode={drawerMode}
    />}

    <CruiseWhenSelect
      ref={whenRef}
      onChange={onChange}
      dropdownAnchorRef={dropdownAnchorRef}
      drawerMode={drawerMode}
    />
  </>
}

export default CruiseSearchInputs
